import { gradientBorderBackground } from '#app/utilities/styles.js';
import { AddIcon } from '@contra/icons/icons';
import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type MouseEvent,
  type Ref,
} from 'react';
import { css, styled } from 'styled-components';

const RoundButton = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    padding: 8px;
    border-radius: 45px;
    transition: all 250ms ease;
    background: ${theme.colors.white30};
    color: ${theme.colorsV2.black};
    box-shadow: ${theme.boxShadow.light};
    ${gradientBorderBackground({
      colorStart: theme.colorsV2.white,
      colorStop: theme.colors.white30,
      radius: '45px',
      strokeWidth: 1.5,
    })}
  `}
`;

const Button = styled.button`
  ${({ theme }) => css`
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: center;
    position: relative;
    padding: 8px 0;
    width: 100%;

    & > * + * {
      margin-left: 16px;
    }

    &:focus,
    &:active {
      box-shadow: 0px 0px 4px ${theme.colorsV2.yellow20};

      & > ${RoundButton} {
        background-color: ${theme.colorsV2.yellow20};
      }
    }

    &:hover {
      cursor: pointer;

      & > ${RoundButton} {
        background-color: ${theme.colorsV2.yellow20};
      }
    }
  `}
`;

export const AddButton = forwardRef(
  (
    {
      children,
      hideAddIcon,
      onClick,
      ...props
    }: ComponentPropsWithoutRef<'button'> & {
      readonly hideAddIcon?: boolean;
      readonly onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
    },
    ref: Ref<HTMLButtonElement>,
  ) => {
    return (
      <Button
        onClick={onClick}
        ref={ref}
        {...props}
      >
        {!hideAddIcon && (
          <RoundButton
            aria-label="add icon"
            role="button"
          >
            <AddIcon size={16} />
          </RoundButton>
        )}
        {children}
      </Button>
    );
  },
);
